import type { MouseEvent } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@accure/ui';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { FileDownIcon } from 'lucide-react';

import { Tooltip } from '@/components/ui/tooltip';
import { useNow } from '@/hooks/useNow';
import { formatMoney } from '@/utils/format/money';
import { Card, CardContent } from '@/warranty-v3/components/ui/card';
import type { MetricsGroup } from '@/warranty-v3/entities/metric';
import { useCurrentSystemId } from '@/warranty-v3/hooks/use-current-system-id';
import { DownloadRawDataMutation } from '@/warranty-v3/mutations/download-raw-data';

import { MetricItem } from './metric-item';

export type MetricsGroupBoxProps = {
  group: MetricsGroup;
};

export function MetricsGroupBox({ group }: MetricsGroupBoxProps) {
  const now = useNow();
  const systemId = useCurrentSystemId();
  const downloadRawData = DownloadRawDataMutation.useMutation();

  async function handleDownloadData(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    const download = downloadRawData.mutateAsync({
      systemId,
      groupId: group.id,
    });

    const { file } = await toast.promise(download, {
      pending: 'Downloading data...',
      success: undefined,
      error: 'Failed to download the data. Please try again.',
    });

    saveAs(
      file,
      `${group.name.toLowerCase()}-${format(now, 'yyyy-MM-dd')}.csv`,
    );
  }

  return (
    <Card className="border-gray-200 bg-white py-4">
      <CardContent className="grid h-full grid-rows-[1fr_theme(spacing.12)] gap-6 px-4">
        <div className="space-y-3">
          {group.metrics.map((metric) => (
            <MetricItem key={metric.id} metric={metric} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2">
          <div>
            {group.capedLD !== undefined ? (
              <div className="text-sm font-semibold leading-tight text-gray-600">
                {formatMoney(group.capedLD, 'USD', 0, 2)}
                <br />
                <span className="text-xs font-normal text-gray-500">
                  Liquidated Damages
                </span>
              </div>
            ) : null}
          </div>
          <Tooltip content={`Download ${group.name} logs`}>
            <Button
              size="sm"
              variant="ghost"
              className="size-6 p-1 text-gray-600"
              onClick={handleDownloadData}
            >
              <FileDownIcon className="size-4" />
            </Button>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );
}
